import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import { SET_ONBOARDING } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import axiosBase from 'axios';
import { AuthProps, JWTContextType } from 'types/auth';
import axiosServices from 'utils/axios';

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const verifyToken = (token: string) => {
  if (!token) return false;
  const decoded = jwtDecode<{ exp: number }>(token);
  return typeof decoded.exp === 'number' && decoded.exp > Date.now() / 1000;
};

const setSession = (token?: string | null) => {
  if (token) {
    localStorage.setItem('serviceToken', token); // keep key name for compatibility
    axiosBase.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axiosBase.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const token = window.localStorage.getItem('serviceToken');
        if (token && verifyToken(token)) {
          setSession(token);

          const response = await axiosServices.get('/api/users/me?populate[role]=*&populate[organization][populate]=*');
          const user = response.data;

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: {
                ...user,
                role: user?.role?.name || user?.role
              }
            }
          });
        } else {
          dispatch({ type: LOGOUT });
        }
      } catch (err) {
        console.error(err);
        dispatch({ type: LOGOUT });
      }
    };

    init();
  }, []);

  const login = async (email: string, password: string) => {
    const API_BASE = import.meta.env.VITE_APP_API_URL || 'http://localhost:1337';

    // Login request without any Authorization header
    const response = await axiosBase.post(
      `${API_BASE}/api/auth/local`,
      {
        identifier: email.toLowerCase(),
        password
      },
      {
        transformRequest: [(data, headers) => {
          // Remove any inherited Authorization header
          delete headers['Authorization'];
          return JSON.stringify(data);
        }],
        headers: { 'Content-Type': 'application/json' }
      }
    );

    const { jwt } = response.data;
    setSession(jwt);

    const userResponse = await axiosServices.get('/api/users/me?populate[role]=*&populate[organization][populate]=*');
    const user = userResponse.data;

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          ...user,
          role: user?.role?.name || user?.role
        }
      }
    });
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, username?: string, role?: number) => {
    const API_BASE = import.meta.env.VITE_APP_API_URL || 'http://localhost:1337';

    const finalUsername = username?.trim() || `${firstName} ${lastName}`.trim() || email;

    // Register with only the fields Strapi accepts by default (explicitly no Authorization)
    const response = await axiosBase.post(
      `${API_BASE}/api/auth/local/register`,
      {
        username: finalUsername,
        email,
        password
      },
      {
        transformRequest: [(data, headers) => {
          // Remove any inherited Authorization header
          delete headers['Authorization'];
          return JSON.stringify(data);
        }],
        headers: { 'Content-Type': 'application/json' }
      }
    );

    const { jwt, user: registeredUser } = response.data;
    setSession(jwt);

    // Update user with firstName and lastName using GraphQL
    // Note: registeredUser.id is the numeric ID from registration, not documentId
    const updateMutation = `
      mutation UpdateUser($id: ID!, $data: UsersPermissionsUserInput!) {
        updateUsersPermissionsUser(id: $id, data: $data) {
          __typename
        }
      }
    `;
    const updateData: Record<string, any> = { firstName, lastName };
    if (role) {
      updateData.role = role;
    }

    await axiosServices.post('/graphql', {
      query: updateMutation,
      variables: {
        id: registeredUser.id,
        data: updateData
      }
    });

    // Fetch full user data — retry until the role mutation is visible (Strapi may
    // return stale role on the first request immediately after the GraphQL update).
    let user: any;
    for (let attempt = 0; attempt < 5; attempt++) {
      const userResponse = await axiosServices.get('/api/users/me?populate[role]=*&populate[organization][populate]=*');
      user = userResponse.data;
      const roleName = user?.role?.name ?? user?.role;
      if (!role || roleName === 'HOA') break;
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          ...user,
          role: user?.role?.name || user?.role
        }
      }
    });
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email: string) => {
    console.log('email - ', email);
  };

  const updateProfile = () => {};

  const setUserOrganization = (organization: { documentId: string; name: string }) => {
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          ...state.user,
          organization
        }
      }
    });
  };

  const setOnboardingComplete = (value: boolean) => {
    dispatch({ type: SET_ONBOARDING, payload: { isLoggedIn: state.isLoggedIn, onboardingComplete: value } });
  };

  const refreshUser = async () => {
    const token = window.localStorage.getItem('serviceToken');
    if (!token) return;
    try {
      const response = await axiosServices.get('/api/users/me?populate[role]=*&populate[organization][populate]=*');
      const user = response.data;

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: {
            ...user,
            role: user?.role?.name || user?.role
          }
        }
      });
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext value={{ ...state, login, logout, register, resetPassword, updateProfile, setUserOrganization, setOnboardingComplete, refreshUser }}>{children}</JWTContext>;
};

export default JWTContext;
